import React, { useState, useEffect } from 'react';

/**
 * Normalisiert einen Wert auf eine Skala von 0 bis 1
 */
export function normalize(value, min, max) {
  const clampedValue = Math.min(Math.max(value, min), max);
  return (clampedValue - min) / (max - min);
}

/**
 * Berechnet den SunnyScore basierend auf Wetterdaten
 */
export function calculateSunnyScore(sonneneinstrahlung, niederschlag, luftfeuchtigkeit, windgeschwindigkeit, monat) {
  // Parameter normalisieren
  const norm_sonneneinstrahlung = normalize(
    Math.min(Math.max(sonneneinstrahlung, 0), 306), 0, 306);
  const norm_niederschlag = 1 - normalize(
    Math.min(Math.max(niederschlag, 0), 0.5), 0, 0.5);
  const norm_luftfeuchtigkeit = 1 - normalize(
    Math.min(Math.max(luftfeuchtigkeit, 40), 100), 40, 100);
  const norm_windgeschwindigkeit = 1 - normalize(
    Math.min(Math.max(windgeschwindigkeit, 0), 10), 0, 10);
  
  // Saisonale Gewichtungen
  let gewicht_sonneneinstrahlung = 0.5;
  let gewicht_niederschlag = 0.3;
  let gewicht_luftfeuchtigkeit = 0.1;
  let gewicht_windgeschwindigkeit = 0.1;
  
  // Anpassung der Gewichtungen je nach Saison
  if (monat >= 11 || monat <= 2) {
    // Winter
    gewicht_sonneneinstrahlung = 0.6;
    gewicht_niederschlag = 0.2;
    gewicht_windgeschwindigkeit = 0.15;
    gewicht_luftfeuchtigkeit = 0.05;
  } else if (monat >= 3 && monat <= 5 || monat >= 9 && monat <= 10) {
    // Frühling und Herbst
    gewicht_sonneneinstrahlung = 0.5;
    gewicht_niederschlag = 0.3;
    gewicht_windgeschwindigkeit = 0.1;
    gewicht_luftfeuchtigkeit = 0.1;
  } else {
    // Sommer
    gewicht_sonneneinstrahlung = 0.4;
    gewicht_niederschlag = 0.4;
    gewicht_windgeschwindigkeit = 0.1;
    gewicht_luftfeuchtigkeit = 0.1;
  }
  
  // Berechnung des gewichteten Scores (0-1)
  const score = 
    norm_sonneneinstrahlung * gewicht_sonneneinstrahlung +
    norm_niederschlag * gewicht_niederschlag +
    norm_luftfeuchtigkeit * gewicht_luftfeuchtigkeit +
    norm_windgeschwindigkeit * gewicht_windgeschwindigkeit;
  
  // Skalierung auf 1-100
  return Math.round(score * 99) + 1;
}

/**
 * Gibt eine verbale Bewertung des SunnyScores zurück
 */
export function getSunnyScoreCategory(score) {
  if (score >= 85) return "Ausgezeichnet";
  if (score >= 70) return "Sehr gut";
  if (score >= 55) return "Gut";
  if (score >= 40) return "Mäßig";
  if (score >= 25) return "Schlecht";
  return "Sehr schlecht";
}

/**
 * Gibt eine Empfehlung basierend auf dem SunnyScore zurück
 */
export function getSunnyScoreRecommendation(score) {
  const category = getSunnyScoreCategory(score);
  
  switch(category) {
    case "Ausgezeichnet":
      return "Perfektes Wetter zum Radfahren! Genießen Sie die idealen Bedingungen und planen Sie auch längere Touren ohne Bedenken.";
    case "Sehr gut":
      return "Sehr gute Bedingungen für eine Radtour. Nehmen Sie leichte Kleidung mit, da das Wetter angenehm ist.";
    case "Gut":
      return "Gute Bedingungen zum Radfahren, aber mit kleineren Einschränkungen. Eine leichte Jacke könnte nützlich sein.";
    case "Mäßig":
      return "Mäßige Wetterbedingungen für Radfahrer. Bereiten Sie sich auf wechselnde Bedingungen vor.";
    case "Schlecht":
      return "Ungünstige Bedingungen für längere Fahrradtouren. Nur notwendige Fahrten empfohlen.";
    case "Sehr schlecht":
      return "Sehr ungeeignete Wetterbedingungen zum Radfahren. Wenn möglich, alternative Transportmittel wählen.";
    default:
      return "";
  }
}

/**
 * Gibt ein Emoji für den SunnyScore zurück
 */
export function getSunnyScoreEmoji(score) {
  if (score >= 85) return "☀️";
  if (score >= 70) return "🌤️";
  if (score >= 55) return "⛅";
  if (score >= 40) return "🌥️";
  if (score >= 25) return "🌧️";
  return "🌩️";
}

// Hauptkomponente für den SunnyScore
const SunnyScore = ({ wetterDaten, prognoseDaten }) => {
  const [score, setScore] = useState(0);
  const [category, setCategory] = useState("");
  const [emoji, setEmoji] = useState("");
  const [recommendation, setRecommendation] = useState("");
  
  useEffect(() => {
    if (wetterDaten) {
      // Aktuelle Daten verwenden
      const calculatedScore = calculateSunnyScore(
        wetterDaten.sonneneinstrahlung,
        wetterDaten.niederschlag,
        wetterDaten.luftfeuchtigkeit,
        wetterDaten.windgeschwindigkeit,
        wetterDaten.monat || new Date().getMonth() + 1
      );
      
      setScore(calculatedScore);
      setCategory(getSunnyScoreCategory(calculatedScore));
      setEmoji(getSunnyScoreEmoji(calculatedScore));
      setRecommendation(getSunnyScoreRecommendation(calculatedScore));
    }
  }, [wetterDaten]);
  
  // Farbverlauf für den Score (von Rot über Gelb zu Grün)
  const getScoreColor = (score) => {
    if (score < 33) return "#ff4d4d"; // Rot
    if (score < 66) return "#ffcc00"; // Gelb
    return "#4CAF50"; // Grün
  };
  
  // Styling für den Kreis-Indikator
  const circleStyle = {
    width: "150px",
    height: "150px",
    borderRadius: "50%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: "white",
    background: `conic-gradient(${getScoreColor(score)} ${score}%, #e0e0e0 0)`,
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    position: "relative"
  };
  
  // Innerer weißer Kreis für bessere Lesbarkeit
  const innerCircleStyle = {
    position: "absolute",
    width: "120px",
    height: "120px",
    borderRadius: "50%",
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: "#333",
    zIndex: 1
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear().toString().substring(2)}`;
  };
  
  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-bold mb-4">SunnyScore: Fahrrad-Wetter-Index</h2>
      
      <div className="flex flex-col items-center mb-4">
        <div style={circleStyle}>
          <div style={innerCircleStyle}>
            <div className="text-3xl font-bold">{score}</div>
            <div className="text-xl">{emoji}</div>
          </div>
        </div>
        
        <h3 className="text-lg font-semibold mt-4">{category}</h3>
      </div>
      
      <div className="p-3 bg-gray-100 rounded-md mb-4">
        <p className="text-gray-800">{recommendation}</p>
      </div>
      
      {/* Aktuelle Wetterwerte anzeigen */}
      {wetterDaten && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-blue-50 p-2 rounded">
            <div className="text-sm text-gray-600">Sonneneinstrahlung</div>
            <div className="font-semibold">{Math.round(wetterDaten.sonneneinstrahlung)} W/m²</div>
          </div>
          <div className="bg-blue-50 p-2 rounded">
            <div className="text-sm text-gray-600">Niederschlag</div>
            <div className="font-semibold">{wetterDaten.niederschlag} mm</div>
          </div>
          <div className="bg-blue-50 p-2 rounded">
            <div className="text-sm text-gray-600">Luftfeuchtigkeit</div>
            <div className="font-semibold">{Math.round(wetterDaten.luftfeuchtigkeit)}%</div>
          </div>
          <div className="bg-blue-50 p-2 rounded">
            <div className="text-sm text-gray-600">Windgeschwindigkeit</div>
            <div className="font-semibold">{wetterDaten.windgeschwindigkeit} m/s</div>
          </div>
        </div>
      )}
      
      {/* Prognose für kommende Tage */}
      {prognoseDaten && prognoseDaten.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2">Prognose für die nächsten Tage</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-3 text-left">Datum</th>
                  <th className="py-2 px-3 text-center">SunnyScore</th>
                  <th className="py-2 px-3 text-center">Bewertung</th>
                </tr>
              </thead>
              <tbody>
                {prognoseDaten.map((tag, index) => {
                  const tagScore = calculateSunnyScore(
                    tag.sonneneinstrahlung,
                    tag.niederschlag,
                    tag.luftfeuchtigkeit,
                    tag.windgeschwindigkeit,
                    new Date(tag.datum).getMonth() + 1
                  );
                  
                  return (
                    <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : ""}>
                      <td className="py-2 px-3">
                        <div>{formatDate(tag.datum)}</div>
                        <div className="text-xs text-gray-600">{tag.wochentag}</div>
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span style={{ color: getScoreColor(tagScore), fontWeight: "bold" }}>{tagScore}</span>
                        <span style={{ marginLeft: "5px" }}>{getSunnyScoreEmoji(tagScore)}</span>
                      </td>
                      <td className="py-2 px-3 text-center">{getSunnyScoreCategory(tagScore)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SunnyScore;